<script lang="ts" setup>
/**
 * This component will render either SurfacePostBeingShowed or SurfacePostBeingEdited depend
 * on the if the post is being edited.
 * The two inner components should have the logic and vuex actions that related to the post
 * and interactions on the post itself:
 * - modify content of the post (including reactions, comments, moderation)
 * - change the toggle status of panels and dropdowns
 * This component should only have the logic and vuex actions that related to post appearance
 * and the interactions between posts:
 * - decide classes and styles
 * - modify size/position of post
 * - modify connections for freeform format
 */
import appCan from '@@/bits/app_can'
import device from '@@/bits/device'
import { yieldToMain } from '@@/bits/global'
import { isUrl } from '@@/bits/url'
import { defineAsyncComponent } from '@@/bits/vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceMapStore } from '@@/pinia/surface_map'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostActionStore } from '@@/pinia/surface_post_action'
import { useSurfacePostConnectionStore } from '@@/pinia/surface_post_connection'
import { useReactionsStore } from '@@/pinia/surface_reactions'
import type { Id, Post } from '@@/types'
import SurfaceLazy from '@@/vuecomponents/SurfaceLazy.vue'
import SurfacePostBeingShowed from '@@/vuecomponents/SurfacePostBeingShowed.vue'
import { useDraggablePost } from '@@/vuecomposables/useDraggablePost'
import { useResizablePost } from '@@/vuecomposables/useResizablePost'
import { storeToRefs } from 'pinia'
import { computed, nextTick, onMounted, ref, watch } from 'vue'

const PostConnectionOverlay = defineAsyncComponent(() => import('@@/vuecomponents/PostConnectionOverlay.vue'))

const props = withDefaults(
  defineProps<{
    index: number
    post?: Post
    isLast?: boolean
    putAttachmentOnTop?: boolean
    fitHeightToContainer?: boolean
    verticallyCenterContent?: boolean
    xShadow?: boolean
    postReactionIds?: Id[]
    postCommentIds?: Id[]
    lazyRender?: boolean
    minPostHeight?: number
    minPostWidth?: number
  }>(),
  {
    post: () => ({}) as Post,
    isLast: false,
    putAttachmentOnTop: false,
    fitHeightToContainer: false,
    verticallyCenterContent: false,
    xShadow: true,
    postReactionIds: () => [],
    postCommentIds: () => [],
    lazyRender: false,
    minPostHeight: 0,
    minPostWidth: 0,
  },
)

const emit = defineEmits<{
  (name: 'last-post-mounted'): void
  (name: 'post-fully-mounted', params: { postId: Id; height: number }): void
  (name: 'focus'): void
}>()

const resizablePost = useResizablePost(props.post)
const draggablePost = useDraggablePost(props.post)

const surfacePostActionStore = useSurfacePostActionStore()
const { postUnderActionCid, postBeingTransferredCid, postBeingCopiedCid } = storeToRefs(surfacePostActionStore)

const reactionsStore = useReactionsStore()
const { postIdBeingReactedTo } = storeToRefs(reactionsStore)

const surfacePostConnectionStore = useSurfacePostConnectionStore()
const { connections, postToConnectFromId } = storeToRefs(surfacePostConnectionStore)

const surfaceStore = useSurfaceStore()
const { user, format, xAuthor, isStream, isGrid } = storeToRefs(surfaceStore)

const surfacePermissionsStore = useSurfacePermissionsStore()
const { canIModerate, canIWrite } = storeToRefs(surfacePermissionsStore)
const surfaceMapStore = useSurfaceMapStore()
const { postWithLocationBeingEditedCid } = storeToRefs(surfaceMapStore)

const label = ref<string>('')
const isLayoutStable = ref<boolean>(false)
const rootElRef = ref<InstanceType<typeof SurfaceLazy> | HTMLElement>()
const rootEl = computed<HTMLElement>(() => {
  if (props.lazyRender) {
    return (rootElRef.value as InstanceType<typeof SurfaceLazy>)?.$el as HTMLElement
  }
  return rootElRef.value as HTMLElement
})

const isBeingActedUpon = computed<boolean>(() => {
  return (
    postBeingTransferredCid.value === props.post.cid ||
    postBeingCopiedCid.value === props.post.cid ||
    postToConnectFromId.value === props.post.id ||
    postWithLocationBeingEditedCid.value === props.post.cid
  )
})

const isBeingReacted = computed<boolean>(() => postIdBeingReactedTo.value === props.post.id)

const isAppActingOnPost = computed<boolean>(() => {
  return (
    device.app &&
    appCan('postMessage') &&
    !!postUnderActionCid.value &&
    Number(props.post.id) === Number(postUnderActionCid.value)
  )
})

const wasWrittenByCurrentUser = computed<boolean>(() => !!user.value.id && props.post.author_id == user.value.id)

const isEditableByCurrentUser = computed<boolean>(
  () => canIModerate.value || (wasWrittenByCurrentUser.value && canIWrite.value),
)

const hasAttachment = computed<boolean>(() => {
  const attachment = props.post.attachment
  return !!attachment && isUrl(attachment)
})

const xConnect = computed<boolean>(() => {
  return (
    !!postToConnectFromId.value &&
    postToConnectFromId.value !== props.post.id &&
    props.post.id != null &&
    !areConnected(props.post.id, postToConnectFromId.value)
  )
})

const styles = computed<any>(() => {
  const styles = {} as any
  if (format.value === 'free') {
    if (isBeingActedUpon.value || isAppActingOnPost.value || isBeingReacted.value) {
      styles['z-index'] = 12000
    } else {
      styles['z-index'] = 10000 - props.index
    }
    // handle size
    const postWidth = props.post.width || 256
    styles.width = postWidth + 'px'
    // handle positioning
    const postTop = props.post.top !== null ? props.post.top : 16 * props.index
    const postLeft = props.post.left !== null ? props.post.left : 16 * props.index
    styles.top = postTop + 'px'
    styles.left = postLeft + 'px'
  } else if (isGrid.value) {
    styles.height = isLayoutStable.value ? 'auto' : props.minPostHeight + 'px'
  }
  return styles
})

const classes = computed<any>(() => {
  return {
    'surface-post': true,
    wish: true,
    'select-none': true,
    // this class must match the border radius class used for SurfacePostBeingShowed
    'rounded-2xl': isStream.value,
    'rounded-xl': !isStream.value,
    'is-animated': isBeingActedUpon.value,
    pulsate: isBeingActedUpon.value || isAppActingOnPost.value,
    'wish-editable': isEditableByCurrentUser.value,
    'wish-mine': wasWrittenByCurrentUser.value,
    'with-attachment': hasAttachment.value,
    'with-author': xAuthor.value,
    'attachment-on-top': props.putAttachmentOnTop,
    'full-height': props.fitHeightToContainer,
    'shadow-elevation-1': props.xShadow,
  }
})

const areConnected = (post1Id: Id, post2Id: Id): boolean => {
  return (
    connections.value.filter((c) => {
      return (
        (c.from_wish_id === post1Id && c.to_wish_id === post2Id) ||
        (c.from_wish_id === post2Id && c.to_wish_id === post1Id)
      )
    }).length > 0
  )
}

const handleConnect = (_label: string): void => {
  label.value = _label
  selectToConnect()
}

const selectToConnect = (): void => {
  if (postToConnectFromId.value != null && props.post.id != null) {
    useSurfacePostConnectionStore().completeConnection({ postToConnectToId: props.post.id, label: label.value })
  }
}

watch(isEditableByCurrentUser, (newValue: boolean) => {
  if (format.value === 'free' && newValue) {
    resizablePost.initializeResizable(rootEl.value, device.mousable)
  } else {
    resizablePost.destroyResizable()
  }
})

const isReadyToMountSurfacePostBeingShowed = ref(false)

onMounted(async () => {
  await yieldToMain()
  // We yield execution back to the browser before mounting the internal components to avoid blocking the main thread.
  // This is to avoid high TBT (Total Blocking Time), which contributes negatively to the Core Web Vitals.
  isReadyToMountSurfacePostBeingShowed.value = true
  if (format.value === 'free' && isEditableByCurrentUser.value) {
    resizablePost.initializeResizable(rootEl.value, device.mousable)
    draggablePost.initializeDraggable(rootEl.value)
  } else {
    resizablePost.destroyResizable()
    draggablePost.destroyDraggable()
  }
  if (props.isLast) {
    emit('last-post-mounted')
  }
  nextTick(() => {
    isLayoutStable.value = true
  })

  // We wait 5 seconds before emitting a post-fully-mounted event to indicate that post has been fully rendered, so we can use the height of the element as the "stable" height of the post.
  // But 5s is just an arbitrary number.
  // Some alternatives we considered but decided against:
  // 1. nextTick: nextTick is enough to just the base post to get rendered, but not for any async components
  //    nested inside it, so that height may not be stable. We could run nextTick a couple of times, but that
  //    would be brittle if we nest any more async components in the future.
  // 2. Emitting a mounted event for the nested components: this might be the most accurate, but it also involves
  //    modification to every internal component inside SurfaceCanvasPost.
  // 3. onvisibilitychange: regardless of when the posts load, we only call the endpoint when the user leaves/closes
  //    the tab. But this could lead to us calling endoint prematurely if the user leaves the tab before the
  //    posts have fully loaded.
  setTimeout(() => {
    const element = rootEl.value
    if (element) {
      const height = element.getBoundingClientRect().height
      if (height > 0 && props.post.id != null) {
        emit('post-fully-mounted', { postId: props.post.id, height })
      }
    }
  }, 5000)
})
</script>

<template>
  <SurfaceLazy
    v-if="lazyRender"
    :id="'wish-' + (post.id || 'new')"
    ref="rootElRef"
    :min-height="minPostHeight"
    :min-width="minPostWidth"
    :class="classes"
    :style="styles"
    :data-view-mode="'show'"
    :data-post-cid="post.cid"
    :data-pinned="String(!!post.pinned_at)"
    :data-rank="post.sort_index"
    :data-index="index + 1"
    :data-section-id="post.wall_section_id"
    :data-color="post.color"
  >
    <SurfacePostBeingShowed v-bind="$props" @focus="emit('focus')" v-if="isReadyToMountSurfacePostBeingShowed" />
    <PostConnectionOverlay v-if="xConnect" @connect="handleConnect" />
  </SurfaceLazy>
  <div
    v-else
    :id="'wish-' + (post.id || 'new')"
    ref="rootElRef"
    :class="classes"
    :style="styles"
    :data-view-mode="'show'"
    :data-post-cid="post.cid"
    :data-pinned="String(!!post.pinned_at)"
    :data-rank="post.sort_index"
    :data-index="index + 1"
    :data-section-id="post.wall_section_id"
    :data-color="post.color"
  >
    <SurfacePostBeingShowed v-bind="$props" @focus="emit('focus')" v-if="isReadyToMountSurfacePostBeingShowed" />
    <PostConnectionOverlay v-if="xConnect" @connect="handleConnect" />
  </div>
</template>

<style lang="scss">
.attachment-on-top {
  .real-wish {
    display: flex;
    flex-direction: column;

    .surface-post-attachment {
      order: -1;
      flex-shrink: 0;
    }
    .surface-post-author {
      order: -2;
      flex-shrink: 0;
    }
    .words {
      flex-grow: 1;
    }
  }
}

.attachment-on-top.with-attachment {
  .real-wish {
    .author + .words {
      padding-top: 12px;
    }
  }
}
</style>

<style lang="scss" scoped>
.full-height {
  height: 100%;
}

// need this so dragging on fire won't leave a weird trail: https://www.loom.com/share/e5f2cd1ea25d4021b7d2c20559e1318d
// looks like it's caused by rotating the container without rotating the child so the child is rendered in a weird way
// => rotate the first child slightly so it will be rendered together with the container
.wish.ui-sortable-helper {
  .firefox & {
    & > :first-child {
      transform: rotate(0.1deg);
    }
  }
}
</style>
